import React, { useState, useEffect, useCallback } from "react";
import { useStateValue } from "../../../State";
import "../../Leops/leops.css";

import ContentBoxMUI from "../../../Components/ContentBoxMUI";
import SnackbarComponent from "../../../Components/Snackbar";
import FixedBottomBar from "../../../Components/FixedBottomBar";
import Languages from "../../../translations";

import makeStyles from "@mui/styles/makeStyles";
import Check from "@mui/icons-material/Check";
import Undo from "@mui/icons-material/Undo";
import Fab from "@mui/material/Fab";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { Grid } from "@mui/material";
import { Paper } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import Typography from "@mui/material/Typography";

import SmileyGreen from "../../../images/smileyt-02.svg";
import SmileyYellow from "../../../images/smileyt-03.svg";

import { types } from "../../../Reducers/actionTypes";
import { getProfilePictureData } from "../../../utils";
import ChildRow from "../../Vasu/ChildRow";

import _cloneDeep from "lodash/cloneDeep";

const useStyles = makeStyles(() => ({
  blacktext: {
    color: "black",
    textAlign: "center",
  },
  selected: {
    backgroundColor: "#ffc4a9",
    borderColor: "#c67651",
    borderStyle: "solid",
    borderWidth: "3px",
    textAlign: "center",
    paddingBottom: "8px",
    paddingTop: "10px",
  },
  notselected: {
    backgroundColor: "#fbdcce94",
    textAlign: "center",
    paddingBottom: "10px",
    paddingTop: "10px",
    border: "none",
  },
  img: {
    marginLeft: "auto",
    marginRight: "auto",
    width: "50%",
  },
}));

const SelfAssessmentContainer = (props) => {
  const classes = useStyles();
  const { state, actions } = useStateValue();
  const { children } = state;

  const childId = props.match.params.ChildId;
  const child = children.ChildList.find((c) => c.Id === childId);
  const CombinedPlanId = child?.CombinedPlanId ?? null;
  const profilePicBase64 = getProfilePictureData(state, child?.Id);
  const childColor = child?.BgColor ?? null;
  const childFirstName = child?.FirstName ?? null;
  const childLastName = child?.LastName ?? null;

  const cpState = state.combinedPlan;
  const loadState = cpState.selfAssessmentState;
  const saveState = cpState.selfAssessmentSaveState;
  const serverData = cpState.selfAssessment;

  const [LoadingData, setLoadingData] = useState(false);
  const [CopiedData, setCopiedData] = useState(null);
  const [dirtySeason, setDirtySeason] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [NoteText, setNoteText] = useState(null);

  // Load self-assessment when CombinedPlanId becomes available
  useEffect(() => {
    if (CombinedPlanId && !LoadingData) {
      actions.triggerLoadCombinedPlanSelfAssessment(CombinedPlanId);
      setLoadingData(true);
    }
  }, [CombinedPlanId]);

  useEffect(() => {
    switch (loadState) {
      case types.LOADING_COMBINED_PLAN_SELF_ASSESSMENT:
        break;
      case types.COMBINED_PLAN_SELF_ASSESSMENT_LOAD_OK:
        setLoadingData(false);
        setCopiedData(_cloneDeep(serverData));
        setDirtySeason(null);
        break;
      case types.COMBINED_PLAN_SELF_ASSESSMENT_LOAD_FAILED:
        setLoadingData(false);
        break;
      default:
        break;
    }
  }, [loadState, serverData]);

  useEffect(() => {
    switch (saveState) {
      case types.COMBINED_PLAN_SELF_ASSESSMENT_SAVED_OK:
        setNoteText(Languages.leopsChildEvaluationSaved);
        setShowSnackbar(true);
        setDirtySeason(null);
        actions.triggerCombinedPlanSelfAssessmentHandled();
        actions.triggerLoadCombinedPlanSelfAssessment(CombinedPlanId);
        break;
      case types.COMBINED_PLAN_SELF_ASSESSMENT_SAVED_FAILED:
        setNoteText(Languages.leopsChildEvaluationSaveFailed);
        setShowSnackbar(true);
        actions.triggerCombinedPlanSelfAssessmentHandled();
        break;
      default:
        break;
    }
  }, [saveState]);

  const handleSmileyClick = useCallback(
    (season, sectionIndex, itemIndex, which) => {
      setCopiedData((prev) => {
        if (!prev) return prev;
        const next = _cloneDeep(prev);
        const sections =
          season === "autumn" ? next.AutumnSections : next.SpringSections;
        const item = sections?.[sectionIndex]?.Items?.[itemIndex];
        if (!item || item.Editable === false) return prev;

        if (which === "learning") {
          item.Knowing = item.Learning === false ? false : item.Knowing;
          item.Learning = !item.Learning;
        } else {
          item.Learning = item.Knowing === false ? false : item.Learning;
          item.Knowing = !item.Knowing;
        }
        return next;
      });
      setDirtySeason(season);
    },
    [],
  );

  const cancelSeason = useCallback(() => {
    setCopiedData(_cloneDeep(serverData));
    setDirtySeason(null);
  }, [serverData]);

  const saveSeason = useCallback(
    (season) => {
      if (!CopiedData) return;
      const sections =
        season === "autumn"
          ? CopiedData.AutumnSections
          : CopiedData.SpringSections;
      const items = [];
      (sections || []).forEach((sec) => {
        (sec.Items || []).forEach((it) => {
          items.push({
            QuestionId: it.QuestionId,
            Learning: it.Learning,
            Knowing: it.Knowing,
          });
        });
      });
      actions.triggerSaveCombinedPlanSelfAssessment({
        planId: CombinedPlanId,
        season,
        items,
      });
    },
    [CopiedData, CombinedPlanId],
  );

  const renderSection = (season, section, sectionIndex) => (
    <div key={season + "_" + section.Section} className="mb-4">
      <h6 className="boxed" style={{ marginBottom: "8px" }}>
        {section.Title}
      </h6>
      {section.Items.map((item, itemIndex) => (
        <div key={item.QuestionId} className="naamat p-2">
          <div className="mt-2">{item.RowText}</div>
          <Grid
            container
            spacing={2}
            className="pt-4 mb-2 justify-content-center"
          >
            <Grid item xs={6} sm={4} md={4} lg={4}>
              <Paper
                id={
                  "test_CPSelfAssessment_Learning_" + season + "_" + itemIndex
                }
                component="button"
                elevation={3}
                className={
                  item.Learning ? classes.selected : classes.notselected
                }
                onClick={() =>
                  handleSmileyClick(season, sectionIndex, itemIndex, "learning")
                }
              >
                <img
                  src={SmileyYellow}
                  alt={"SmileyYellow"}
                  className={classes.img}
                ></img>
                {item.Learning && item.Editable === false ? (
                  <LockIcon style={{ color: "#D500F9" }} fontSize="large" />
                ) : null}
                <p className={classes.blacktext}>
                  {Languages.leopsStillTraining}
                </p>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={4} lg={4}>
              <Paper
                id={"test_CPSelfAssessment_Knowing_" + season + "_" + itemIndex}
                component="button"
                elevation={3}
                className={
                  item.Knowing ? classes.selected : classes.notselected
                }
                onClick={() =>
                  handleSmileyClick(season, sectionIndex, itemIndex, "knowing")
                }
              >
                <img
                  src={SmileyGreen}
                  alt={"SmileyGreen"}
                  className={classes.img}
                ></img>
                {item.Knowing && item.Editable === false ? (
                  <LockIcon style={{ color: "#D500F9" }} fontSize="large" />
                ) : null}
                <p className={classes.blacktext}>
                  {Languages.leopsICanAlready}
                </p>
              </Paper>
            </Grid>
          </Grid>
        </div>
      ))}
    </div>
  );

  const renderSeason = (season, heading, sections) => {
    if (!sections || sections.length === 0) return null;
    return (
      <div className="mb-4">
        <Typography variant="h6" style={{ fontWeight: "bold", marginTop: 16 }}>
          {heading}
        </Typography>
        <div className="mb-2">{Languages.leopsAnswerSelectingBestChoice}</div>
        {sections.map((section, idx) => renderSection(season, section, idx))}
      </div>
    );
  };

  const isLocked = CopiedData?.Locked === true;

  // Autumn assessment is filled during the autumn term (Aug-Dec), spring
  // assessment during the spring term (Jan-Jul). Only show the season that
  // matches the current time of year. Mirrors the backend rule where spring
  // is editable only before August (month < 8).
  const isSpringTime = new Date().getMonth() < 7;

  return (
    <ContentBoxMUI>
      <ChildRow
        childpicture={profilePicBase64}
        childFirstName={childFirstName}
        childLastName={childLastName}
        childColor={childColor}
        FormId={CombinedPlanId}
        history={props.history}
      />

      <SnackbarComponent
        verticalPosition={"bottom"}
        show={showSnackbar}
        message={NoteText}
        callback={() => setShowSnackbar(false)}
      />

      {loadState === types.LOADING_COMBINED_PLAN_SELF_ASSESSMENT ||
      !CopiedData ? (
        <Backdrop open={true} style={{ color: "#fff", zIndex: 1200 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <div className={classes.blacktext}>
          {isLocked ? (
            <div
              className="mb-3"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <LockIcon fontSize="small" />
              <span>{Languages.LockLeopsInfo}</span>
            </div>
          ) : null}

          {isSpringTime
            ? renderSeason(
                "spring",
                Languages.LeopsTitle4_2_1,
                CopiedData.SpringSections,
              )
            : renderSeason(
                "autumn",
                Languages.LeopsTitle1_2_1,
                CopiedData.AutumnSections,
              )}

          {(!CopiedData.AutumnSections ||
            CopiedData.AutumnSections.length === 0) &&
          (!CopiedData.SpringSections ||
            CopiedData.SpringSections.length === 0) ? (
            <Typography style={{ marginTop: 24 }}>
              {Languages.errorNotFound}
            </Typography>
          ) : null}

          {dirtySeason ? (
            <FixedBottomBar>
              <div className="container painikkeet row">
                <Fab
                  id="test_CPSelfAssessment_Cancel"
                  style={{
                    color: "white",
                    backgroundColor: "#FF284C",
                    margin: "auto",
                  }}
                  onClick={cancelSeason}
                >
                  <Undo />
                </Fab>
                <Fab
                  id="test_CPSelfAssessment_Save"
                  style={{
                    color: "white",
                    backgroundColor: "#81C784",
                    margin: "auto",
                  }}
                  onClick={() => saveSeason(dirtySeason)}
                >
                  <Check />
                </Fab>
              </div>
            </FixedBottomBar>
          ) : null}
        </div>
      )}
    </ContentBoxMUI>
  );
};

export default SelfAssessmentContainer;
